import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, User, Briefcase, Heart, Eye, EyeOff } from 'lucide-react';
import { Logo } from '../components/Logo';
import Footer from '../components/Footer';

export default function AuthPage() {
    const { user, login, register } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'login');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Login state
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');

    // Register state
    const [regName, setRegName] = useState('');
    const [regFirstName, setRegFirstName] = useState('');
    const [regLastName, setRegLastName] = useState('');
    const [regEmail, setRegEmail] = useState('');
    const [regPassword, setRegPassword] = useState('');
    const [regRole, setRegRole] = useState(searchParams.get('role') || '');
    const [regCompanyName, setRegCompanyName] = useState('');
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [showLoginPass, setShowLoginPass] = useState(false);
    const [showRegPass, setShowRegPass] = useState(false);

    useEffect(() => {
        if (user) navigate(`/${user.role}`, { replace: true });
    }, [user, navigate]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const u = await login(loginEmail, loginPassword);
            navigate(`/${u.role}`, { replace: true });
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setError('');
        if (!regRole) {
            setError('Please select a role');
            return;
        }
        if (!agreedToTerms) {
            setError('You must agree to the Terms of Service and Privacy Policy');
            return;
        }
        let nameToSubmit;
        if (regRole === 'worker') {
            const firstName = regFirstName.trim();
            const lastName = regLastName.trim();
            if (!firstName || !lastName) {
                setError('Please enter both your first name and last name');
                return;
            }
            nameToSubmit = `${firstName} ${lastName}`;
        } else if (regRole === 'employer') {
            nameToSubmit = regCompanyName;
        } else {
            nameToSubmit = regName;
        }
        setLoading(true);
        try {
            const u = await register(regEmail, regPassword, nameToSubmit, regRole);
            navigate(`/${u.role}`, { replace: true });
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    const roles = [
        { value: 'worker', label: 'Worker', icon: <User className="w-5 h-5" />, desc: 'Looking for work in the Gulf' },
        { value: 'employer', label: 'Employer', icon: <Briefcase className="w-5 h-5" />, desc: 'Hire verified blue collar talent' },
        { value: 'medical', label: 'Medical Partner', icon: <Heart className="w-5 h-5" />, desc: 'Providing medical checks' },
    ];

    return (
        <div className="min-h-screen bg-white flex flex-col">
            {/* Header */}
            <div className="border-b border-zinc-200 px-6 py-4">
                <div className="max-w-md mx-auto flex items-center gap-4">
                    <button onClick={() => navigate('/')} className="text-zinc-400 hover:text-zinc-900 transition-colors" data-testid="auth-back-btn">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <Logo size="md" data-testid="auth-logo" />
                </div>
            </div>

            {/* Auth Form */}
            <div className="flex-1 flex items-start justify-center pt-12 px-6">
                <div className="w-full max-w-md">
                    <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setError(''); }} data-testid="auth-tabs">
                        <TabsList className="grid grid-cols-2 w-full rounded-none h-12 bg-zinc-100">
                            <TabsTrigger value="login" className="rounded-none h-10 font-semibold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="login-tab">
                                Sign In
                            </TabsTrigger>
                            <TabsTrigger value="register" className="rounded-none h-10 font-semibold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="register-tab">
                                Register
                            </TabsTrigger>
                        </TabsList>

                        {error && (
                            <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="auth-error">
                                {error}
                            </div>
                        )}

                        <TabsContent value="login" className="mt-6">
                            <form onSubmit={handleLogin} className="space-y-5">
                                <div>
                                    <Label htmlFor="login-email" className="text-sm font-semibold">Email</Label>
                                    <Input
                                        id="login-email"
                                        type="email"
                                        value={loginEmail}
                                        onChange={(e) => setLoginEmail(e.target.value)}
                                        className="mt-2 h-14 rounded-none text-base"
                                        placeholder="your@email.com"
                                        required
                                        data-testid="login-email-input"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="login-password" className="text-sm font-semibold">Password</Label>
                                    <div className="relative mt-2">
                                        <Input
                                            id="login-password"
                                            type={showLoginPass ? 'text' : 'password'}
                                            value={loginPassword}
                                            onChange={(e) => setLoginPassword(e.target.value)}
                                            className="h-14 rounded-none text-base pr-12"
                                            placeholder="Enter password"
                                            required
                                            data-testid="login-password-input"
                                        />
                                        <button type="button" onClick={() => setShowLoginPass(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-900" data-testid="toggle-login-password">
                                            {showLoginPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                    <a href="/forgot-password" className="text-xs text-zinc-500 hover:text-zinc-900 underline mt-2 inline-block" data-testid="forgot-password-link">Forgot password?</a>
                                </div>
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full h-14 rounded-none bg-zinc-900 text-white text-base font-semibold hover:bg-zinc-800"
                                    data-testid="login-submit-btn"
                                >
                                    {loading ? 'Signing in...' : 'Sign In'}
                                </Button>
                                <p className="text-center text-xs text-zinc-500 pt-1">
                                    Need help?{' '}
                                    <a href="/faq" className="text-zinc-700 hover:text-zinc-900 underline font-semibold" data-testid="auth-faq-link">
                                        Visit our FAQ
                                    </a>
                                </p>
                            </form>
                        </TabsContent>

                        <TabsContent value="register" className="mt-6">
                            <form onSubmit={handleRegister} className="space-y-5">
                                {/* Role Selection */}
                                <div>
                                    <Label className="text-sm font-semibold">I am a</Label>
                                    <div className="grid grid-cols-1 gap-3 mt-2">
                                        {roles.map((r) => (
                                            <button
                                                key={r.value}
                                                type="button"
                                                onClick={() => setRegRole(r.value)}
                                                className={`flex items-center gap-4 p-4 border text-left transition-colors ${
                                                    regRole === r.value
                                                        ? 'border-zinc-900 bg-zinc-50'
                                                        : 'border-zinc-200 hover:border-zinc-400'
                                                }`}
                                                data-testid={`role-${r.value}-btn`}
                                            >
                                                <div className={`w-10 h-10 flex items-center justify-center ${
                                                    regRole === r.value ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600'
                                                }`}>
                                                    {r.icon}
                                                </div>
                                                <div>
                                                    <div className="font-semibold text-sm">{r.label}</div>
                                                    <div className="text-xs text-zinc-500">{r.desc}</div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {regRole === 'worker' ? (
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label htmlFor="reg-first-name" className="text-sm font-semibold">First Name</Label>
                                            <Input
                                                id="reg-first-name"
                                                type="text"
                                                value={regFirstName}
                                                onChange={(e) => setRegFirstName(e.target.value)}
                                                className="mt-2 h-14 rounded-none text-base"
                                                placeholder="First name"
                                                required
                                                data-testid="register-first-name-input"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="reg-last-name" className="text-sm font-semibold">Last Name</Label>
                                            <Input
                                                id="reg-last-name"
                                                type="text"
                                                value={regLastName}
                                                onChange={(e) => setRegLastName(e.target.value)}
                                                className="mt-2 h-14 rounded-none text-base"
                                                placeholder="Last name"
                                                required
                                                data-testid="register-last-name-input"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <Label htmlFor="reg-name" className="text-sm font-semibold">{regRole === 'employer' ? 'Company Name' : 'Full Name'}</Label>
                                        <Input
                                            id="reg-name"
                                            type="text"
                                            value={regRole === 'employer' ? regCompanyName : regName}
                                            onChange={(e) => regRole === 'employer' ? setRegCompanyName(e.target.value) : setRegName(e.target.value)}
                                            className="mt-2 h-14 rounded-none text-base"
                                            placeholder={regRole === 'employer' ? 'Your company name' : 'Your full name'}
                                            required
                                            data-testid="register-name-input"
                                        />
                                    </div>
                                )}
                                <div>
                                    <Label htmlFor="reg-email" className="text-sm font-semibold">Email</Label>
                                    <Input
                                        id="reg-email"
                                        type="email"
                                        value={regEmail}
                                        onChange={(e) => setRegEmail(e.target.value)}
                                        className="mt-2 h-14 rounded-none text-base"
                                        placeholder="your@email.com"
                                        required
                                        data-testid="register-email-input"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="reg-password" className="text-sm font-semibold">Password</Label>
                                    <div className="relative mt-2">
                                        <Input
                                            id="reg-password"
                                            type={showRegPass ? 'text' : 'password'}
                                            value={regPassword}
                                            onChange={(e) => setRegPassword(e.target.value)}
                                            className="h-14 rounded-none text-base pr-12"
                                            placeholder="Create a password"
                                            required
                                            data-testid="register-password-input"
                                        />
                                        <button type="button" onClick={() => setShowRegPass(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-900" data-testid="toggle-register-password">
                                            {showRegPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>
                                {/* Terms & Privacy */}
                                <label className="flex items-start gap-3 cursor-pointer" data-testid="terms-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={agreedToTerms}
                                        onChange={e => setAgreedToTerms(e.target.checked)}
                                        className="mt-1 w-5 h-5 accent-zinc-900"
                                        data-testid="terms-checkbox"
                                    />
                                    <span className="text-sm text-zinc-600">
                                        I agree to the{' '}
                                        <a href="/terms" target="_blank" className="underline font-semibold text-zinc-900" data-testid="terms-link">Terms of Service</a>
                                        {' '}and{' '}
                                        <a href="/privacy" target="_blank" className="underline font-semibold text-zinc-900" data-testid="privacy-link">Privacy Policy</a>
                                    </span>
                                </label>
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full h-14 rounded-none bg-zinc-900 text-white text-base font-semibold hover:bg-zinc-800"
                                    data-testid="register-submit-btn"
                                >
                                    {loading ? 'Creating account...' : 'Create Account'}
                                </Button>
                            </form>
                        </TabsContent>
                    </Tabs>
                </div>
            </div>
            <Footer />
        </div>
    );
}
